// Third-party library imports
import {
    createSlice,
    PayloadAction,
    createSelector
} from '@reduxjs/toolkit';

// Local imports - Types
import {
    WebhookConfigurationState,
    CompanyWebhooksConfiguration,
} from './types';

// Local imports - Utility functions & interfaces
import { ResponseModalObject, DynamicObject } from '../../utils/commonInterfaces';

// Local imports - Redux store
import type { RootState } from '../storeTypes';

// Action prefix for consistency
const actionPrefix = 'webhookConfiguration';

// Type-safe initialState!
export const initialState: WebhookConfigurationState = {
    loading: false,
    data: null,
    saveLoading: false,
    hmacLoading: false,
    aesLoading: false,
};

// everything will remain type-safe.
const webhookConfigurationSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        // Get Webhooks Configuration
        getWebhooksConfigurationRequestAction: (state) => {
            state.loading = true;
        },
        getWebhooksConfigurationSuccessAction: (
            state,
            action: PayloadAction<CompanyWebhooksConfiguration>
        ) => {
            state.loading = false;
            state.data = action.payload;
        },
        getWebhooksConfigurationFailedAction: (
            state,
            _action: PayloadAction<string>
        ) => {
            state.loading = false;
        },

        // Save Webhooks Configuration
        saveWebhooksConfigurationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: CompanyWebhooksConfiguration;
                    callback?: (response: ResponseModalObject) => void;
                }>
            ) => {
                state.saveLoading = true;
            },
            prepare: (
                payload: CompanyWebhooksConfiguration,
                callback?: (response: ResponseModalObject) => void
            ) => ({
                payload: { payload, callback }
            })
        },
        saveWebhooksConfigurationResponseAction: (state) => {
            state.saveLoading = false;
        },

        // HMAC Signature Actions - Request (set loading)
        generateHMACSignatureRequest: (
            state,
            _action: PayloadAction<{ callback?: (response: DynamicObject) => void }>
        ) => {
            state.hmacLoading = true;
        },
        rotateHMACSignatureRequest: (
            state,
            _action: PayloadAction<{ callback?: (response: DynamicObject) => void }>
        ) => {
            state.hmacLoading = true;
        },
        removeHMACSignatureRequest: (
            state,
            _action: PayloadAction<{ callback?: (response: ResponseModalObject) => void }>
        ) => {
            state.hmacLoading = true;
        },

        // HMAC Signature Actions - Response (clear loading)
        generateHMACSignatureResponseAction: (state) => {
            state.hmacLoading = false;
        },
        rotateHMACSignatureResponseAction: (state) => {
            state.hmacLoading = false;
        },
        removeHMACSignatureResponseAction: (state) => {
            state.hmacLoading = false;
        },

        // AES Key Actions - Request (set loading)
        generateAESKeyRequest: (
            state,
            _action: PayloadAction<{ callback?: (response: DynamicObject) => void }>
        ) => {
            state.aesLoading = true;
        },
        rotateAESKeyRequest: (
            state,
            _action: PayloadAction<{ callback?: (response: DynamicObject) => void }>
        ) => {
            state.aesLoading = true;
        },
        removeAESKeyRequest: (
            state,
            _action: PayloadAction<{ callback?: (response: ResponseModalObject) => void }>
        ) => {
            state.aesLoading = true;
        },

        // AES Key Actions - Response (clear loading)
        generateAESKeyResponseAction: (state) => {
            state.aesLoading = false;
        },
        rotateAESKeyResponseAction: (state) => {
            state.aesLoading = false;
        },
        removeAESKeyResponseAction: (state) => {
            state.aesLoading = false;
        },
    },
});

// Extract the action creators object and the reducer
const { actions, reducer } = webhookConfigurationSlice;

// Export actions from slice
export const {
    getWebhooksConfigurationRequestAction,
    getWebhooksConfigurationSuccessAction,
    getWebhooksConfigurationFailedAction,
    saveWebhooksConfigurationRequestAction,
    saveWebhooksConfigurationResponseAction,
    generateHMACSignatureRequest,
    generateHMACSignatureResponseAction,
    rotateHMACSignatureRequest,
    rotateHMACSignatureResponseAction,
    removeHMACSignatureRequest,
    removeHMACSignatureResponseAction,
    generateAESKeyRequest,
    generateAESKeyResponseAction,
    rotateAESKeyRequest,
    rotateAESKeyResponseAction,
    removeAESKeyRequest,
    removeAESKeyResponseAction,
} = actions;

// Export aliases for saga-triggered actions (keeping original naming convention)
// These point to the slice reducers that handle the loading state
export const generateHMACSignatureRequestAction = generateHMACSignatureRequest;
export const rotateHMACSignatureRequestAction = rotateHMACSignatureRequest;
export const removeHMACSignatureRequestAction = removeHMACSignatureRequest;
export const generateAESKeyRequestAction = generateAESKeyRequest;
export const rotateAESKeyRequestAction = rotateAESKeyRequest;
export const removeAESKeyRequestAction = removeAESKeyRequest;

// Selectors
export const getWebhookConfigurationState = (state: RootState) => state.webhookConfiguration;

export const getWebhookConfigurationData = createSelector(
    getWebhookConfigurationState,
    (webhookConfiguration) => webhookConfiguration.data
);

export const getWebhookConfigurationLoading = createSelector(
    getWebhookConfigurationState,
    (webhookConfiguration) => webhookConfiguration.loading
);

export const getWebhookConfigurationSaveLoading = createSelector(
    getWebhookConfigurationState,
    (webhookConfiguration) => webhookConfiguration.saveLoading
);

export const getWebhookConfigurationHMACLoading = createSelector(
    getWebhookConfigurationState,
    (webhookConfiguration) => webhookConfiguration.hmacLoading
);

export const getWebhookConfigurationAESLoading = createSelector(
    getWebhookConfigurationState,
    (webhookConfiguration) => webhookConfiguration.aesLoading
);

export default reducer;
